import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { ShopifyAppConfigService } from "../../utils/services/shopify-app-config-service";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import { inputFieldSx } from "../../constants/fieldStyles";

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidShopName = (shopName) => {
  // Reject if starts with http:// or https:// (matches backend validation)
  if (shopName.startsWith("http://") || shopName.startsWith("https://")) {
    return false;
  }
  return true;
};

const initialFormData = {
  shop_name: "",
  api_key: "",
  api_secret: "",
  name: "",
  business_name: "",
  email: "",
  contact: "+1",
  install_url: "",
};

const ShopifyConfigCreateModal = ({ open, onClose, onCreated, setLoading }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  const handleClose = () => {
    onClose();
    setFormErrors({});
    setFormData(initialFormData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Ensure contact always starts with +1
    if (name === "contact") {
      if (!value.startsWith("+1")) {
        return setFormData({
          ...formData,
          [name]: "+1" + value.replace(/^\+1/, ""),
        });
      }
    }
    setFormErrors((prev) => {
      const { [name]: removedError, ...rest } = prev;
      return rest;
    });
    setFormData({ ...formData, [name]: value });
  };

  const createShopifyConfig = async (data) => {
    setLoading(true);
    try {
      await ShopifyAppConfigService.create(data);
      onCreated();
      handleClose();
      toast.success("Shopify config created successfully!");
    } catch (error) {
      console.error("Create failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const errors = {};

    if (!formData.name) errors.name = "Name is required";
    if (!formData.business_name)
      errors.business_name = "Business name is required";

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (!formData.contact) {
      errors.contact = "Contact is required";
    }
    // else if (!isValidPhoneNumber(formData.contact)) {
    //   errors.contact = "Invalid phone number. Must be +1 followed by 10 digits";
    // }

    if (!formData.shop_name) {
      errors.shop_name = "Shop name is required";
    } else if (!isValidShopName(formData.shop_name)) {
      errors.shop_name = 'Shop name cannot contain "http://" or "https://"';
    } else if (!formData.shop_name.includes("myshopify.com")) {
      errors.shop_name = 'Shop name must contain "myshopify.com"';
    }

    if (!formData.api_key) errors.api_key = "API key is required";
    if (!formData.api_secret) errors.api_secret = "API secret is required";
    if (!formData.install_url) errors.install_url = "Install URL is required";

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return; // no toast here
    }

    createShopifyConfig(formData);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "var(--bg-primary)",
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        Add Shop Details
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "var(--bg-primary)" }}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!formErrors.name}
              helperText={formErrors.name}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Business Name"
              name="business_name"
              value={formData.business_name}
              onChange={handleChange}
              fullWidth
              required
              error={!!formErrors.business_name}
              helperText={formErrors.business_name}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={async () => {
                if (formData.email && isValidEmail(formData.email)) {
                  try {
                    const res = await ShopifyAppConfigService.checkAvailability(
                      {
                        type: "user",
                        value: formData.email,
                      },
                    );
                    if (!res.available) {
                      setFormErrors((prev) => ({
                        ...prev,
                        email: "Email already exists!",
                      }));
                    } else {
                      setFormErrors((prev) => {
                        const { email, ...rest } = prev;
                        return rest;
                      });
                    }
                  } catch (err) {
                    const message =
                      err?.response?.data?.message ||
                      err?.message ||
                      "Error checking email";
                    setFormErrors((prev) => ({
                      ...prev,
                      email: message,
                    }));
                  }
                } else {
                  setFormErrors((prev) => {
                    const { email, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              fullWidth
              required
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Contact"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              fullWidth
              error={!!formErrors.contact}
              helperText={formErrors.contact}
              sx={inputFieldSx}
              type="tel"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Shop Name"
              name="shop_name"
              value={formData.shop_name}
              onChange={handleChange}
              onBlur={async () => {
                let error = null;

                // Check for http:// or https://
                if (
                  formData.shop_name &&
                  !isValidShopName(formData.shop_name)
                ) {
                  error = 'Shop name cannot contain "http://" or "https://"';
                  setFormErrors((prev) => ({
                    ...prev,
                    shop_name: error,
                  }));
                  return;
                }

                if (
                  formData.shop_name &&
                  formData.shop_name.includes("myshopify.com")
                ) {
                  try {
                    const res = await ShopifyAppConfigService.checkAvailability(
                      {
                        type: "shop",
                        value: formData.shop_name,
                      },
                    );
                    if (!res.available) {
                      setFormErrors((prev) => ({
                        ...prev,
                        shop_name: "Shop name already exists!",
                      }));
                    } else {
                      setFormErrors((prev) => {
                        const { shop_name, ...rest } = prev;
                        return rest;
                      });
                    }
                  } catch (err) {
                    const message =
                      err?.response?.data?.message ||
                      err?.message ||
                      "Error checking shop name";
                    setFormErrors((prev) => ({
                      ...prev,
                      shop_name: message,
                    }));
                  }
                } else {
                  setFormErrors((prev) => {
                    const { shop_name, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              fullWidth
              required
              error={!!formErrors.shop_name}
              helperText={formErrors.shop_name}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title='Shopify domain which contains "myshopify.com" and should not include "http://" or "https://"'>
                      <InfoIcon style={{ cursor: "pointer" }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="API Key"
              name="api_key"
              value={formData.api_key}
              onChange={handleChange}
              fullWidth
              required
              error={!!formErrors.api_key}
              helperText={formErrors.api_key}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="API Secret Key"
              name="api_secret"
              value={formData.api_secret}
              onChange={handleChange}
              fullWidth
              required
              error={!!formErrors.api_secret}
              helperText={formErrors.api_secret}
              sx={inputFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Install URL"
              name="install_url"
              value={formData.install_url}
              onChange={handleChange}
              fullWidth
              required
              placeholder="https://..."
              error={!!formErrors.install_url}
              helperText={formErrors.install_url}
              sx={inputFieldSx}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "var(--bg-primary)" }}>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} color="primary">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopifyConfigCreateModal;
